import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {SplitText} from 'gsap/SplitText';

// Pin progressivo dei pannelli .panel-over-section
function initPanelPin() {
    const panels = gsap.utils.toArray('.panel-over-section');
    panels.forEach((panel, i) => {
        gsap.set(panel, {zIndex: i});
        ScrollTrigger.create({
            trigger: panel, start: 'top top', pin: i < panels.length - 1, pinSpacing: false,
        });
    });
}

// Classi che attivano animazioni CSS sui titoli delle child hero
function initChildHeroTitles() {
    if (document.querySelector('.child-hero-parent-title')) {
        document.querySelectorAll('.child-hero-parent-title')
            .forEach(el => el.classList.add('translate'));
    }
    if (document.querySelector('.child-hero-title')) {
        document.querySelectorAll('.child-hero-title')
            .forEach(el => el.classList.add('animated-title'));
    }
}

// Image toggle — pagina Awards (solo desktop)
function initAwardsVenues() {
    if (!window.matchMedia('(min-width: 992px)').matches) return;

    document.querySelectorAll('.list-premio').forEach(premio => {
        const right = premio.querySelector('.list-premio-right');
        const left = premio.querySelector('.list-premio-left');
        if (!right) return;
        const btn = right.querySelector('.image-toggle');
        const clip = right.querySelector('.image-clip');
        const img = clip && clip.querySelector('img');
        if (!btn || !clip || !img) return;

        let fullH = 0;
        let open = false;

        const centerOffset = () => Math.max(0, (fullH - 80) / 2);

        const init = () => {
            fullH = img.offsetHeight;
            if (fullH) {
                gsap.set(clip, {height: '80px'});
                gsap.set(img, {y: -centerOffset()});
            }
        };

        if (img.complete && img.naturalWidth) {
            init();
        } else {
            img.addEventListener('load', init, {once: true});
        }

        const toggle = () => {
            if (!fullH) return;

            gsap.killTweensOf(clip);
            gsap.killTweensOf(img);

            if (!open) {
                gsap.to(clip, {height: fullH, duration: 0.6, ease: 'power2.inOut'});
                gsap.to(img, {y: 0, duration: 0.6, ease: 'power2.inOut'});
            } else {
                gsap.to(clip, {height: '80px', duration: 0.6, ease: 'power2.inOut'});
                gsap.to(img, {y: -centerOffset(), duration: 0.6, ease: 'power2.inOut'});
            }
            btn.classList.toggle('active', !open);
            premio.classList.toggle('is-open', !open);

            open = !open;
        };

        btn.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();
            toggle();
        });

        if (left) {
            left.addEventListener('click', e => {
                if (e.target.closest('.list-premio-link')) return;
                e.preventDefault();
                toggle();
            });
        }
    });
}

// SplitText sui titoli [data-gsap-anim-title] (.animationTitle legacy)
function initAnimationTitles() {
    const animationTitle = document.querySelectorAll('[data-gsap-anim-title], .animationTitle');
    if (animationTitle.length === 0) {
        return;
    }

    animationTitle.forEach((item) => {
        const targets = resolveTextTargets(item);
        if (!targets.length) return;
        gsap.set(item, {autoAlpha: 1});
        const splitTitle = SplitText.create(targets, {
            type: 'words,lines', mask: 'lines', linesClass: 'line', aria: 'auto',
        });

        const insideHScroll = item.closest('.js-h-outer');
        const delay = getGsapValue(item, 'gsapDelay', insideHScroll ? 0.3 : 0);
        const duration = getGsapValue(item, 'gsapDuration', 2);
        const y = insideHScroll ? 100 : 120;
        // Stato iniziale esplicito sulle linee per evitare flash di contenuto
        // visibile prima del play.
        gsap.set(splitTitle.lines, {y, opacity: 0});
        const playAnim = () => gsap.to(splitTitle.lines, {
            y: 0, opacity: 1, stagger: 0.12, duration, ease: 'power4.out',
        });

        runOnPanelLand(item, playAnim, {delay, start: 'top 85%'});
    });
}

// SplitText sui paragrafi [data-gsap-anim-paragraph] (.animationParagraph legacy)
function initAnimationParagraphs() {
    const animationParagraph = document.querySelectorAll('[data-gsap-anim-paragraph], .animationParagraph');
    if (animationParagraph.length === 0) {
        return;
    }

    document.fonts.ready.then(() => {
        animationParagraph.forEach((item) => {
            const targets = resolveTextTargets(item);
            if (!targets.length) return;
            gsap.set(item, {autoAlpha: 1});
            const splitParagraph = SplitText.create(targets, {
                type: 'words, lines', mask: 'lines', aria: 'auto',
            });

            const insideHScroll = item.closest('.js-h-outer');
            const delay = getGsapValue(item, 'gsapDelay', insideHScroll ? 0.5 : 0);
            const duration = getGsapValue(item, 'gsapDuration', 1.3);
            // Stato iniziale esplicito sulle parole per evitare flash.
            gsap.set(splitParagraph.words, {y: 60, opacity: -0.7});
            const playAnim = () => gsap.to(splitParagraph.words, {
                y: 0, opacity: 1, stagger: 0.04, duration, ease: 'power2.out',
            });

            runOnPanelLand(item, playAnim, {delay, start: 'top 100%'});
        });
    });
}

// Progress bar di scroll in cima alla pagina
function initProgressBar() {
    const track = document.getElementById('pb-track');
    const fill = document.getElementById('pb-fill');
    const footer = document.querySelector('footer');

    if (!track || !fill) {
        return;
    }

    function updateProgressBar() {
        const s = document.documentElement;
        const pct = s.scrollTop / (s.scrollHeight - s.clientHeight);
        fill.style.width = (pct * 100) + '%';

        if (pct > 0.01) {
            track.classList.add('pb-visible');
        } else {
            track.classList.remove('pb-visible');
        }

        if (footer) {
            const footerRect = footer.getBoundingClientRect();
            const viewportCenter = window.innerHeight / 2;

            const centerInsideFooter =
                footerRect.top <= viewportCenter &&
                footerRect.bottom >= viewportCenter;

            if (centerInsideFooter) {
                track.classList.remove('pb-visible');
            }
        }
    }

    window.addEventListener('scroll', updateProgressBar);
    updateProgressBar();
}

// Risolve i target reali per SplitText scendendo dentro al contenitore.
// Serve perché componenti tinyMCE possono iniettare h1-h6/p/li/... o testo nudo:
// senza questo, SplitText applicato al wrapper esterno calcola male le linee
// quando dentro ci sono più block element.
function resolveTextTargets(container) {
    const blocks = container.querySelectorAll('h1, h2, h3, h4, h5, h6, p, li, blockquote, figcaption');
    if (blocks.length > 0) {
        return Array.from(blocks);
    }
    if (container.textContent.trim().length > 0) {
        return [container];
    }
    return [];
}

function getGsapValue(element, key, fallback) {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    const desktopValue = element.dataset[key];
    const mobileValue = element.dataset[`${key}Mobile`];

    return parseFloat(
        isMobile ? (mobileValue ?? desktopValue ?? fallback) : (desktopValue ?? fallback)
    );
}

// Trigger generico per le animazioni data-gsap-*.
// - Dentro un .js-h-outer (horizontal scroll): osserva il .js-h-panel più vicino
//   e chiama runFn quando il panel atterra in viewport (oppure subito se già visibile).
//   Su mobile aggiunge un delay extra per attendere la fine della transizione swipe
//   e usa una soglia più alta sull'IntersectionObserver.
// - Altrimenti: usa ScrollTrigger con start configurabile (default "top 85%").
// playFn deve essere idempotente / pensato per essere chiamato una sola volta.
function runOnPanelLand(element, runFn, opts = {}) {
    const {delay = 0, start = "top 85%"} = opts;
    const insideHScroll = element.closest('.js-h-outer');
    const isMobile = window.matchMedia("(max-width: 991px)").matches;
    // Su mobile l'IntersectionObserver scatta mentre il pannello sta ancora
    // slidando: ritarda l'animazione abbastanza da farla iniziare a transizione
    // (quasi) conclusa.
    const mobileExtraDelay = insideHScroll &&  0.3;
    const totalDelay = delay + mobileExtraDelay;

    const fire = () => {
        if (totalDelay > 0) {
            gsap.delayedCall(totalDelay, runFn);
        } else {
            runFn();
        }
    };

    if (insideHScroll) {
        const panel = element.closest('.js-h-panel') || element;
        const r = panel.getBoundingClientRect();
        // "Già visibile" più stringente: su mobile considero visibile solo se
        // il panel occupa la viewport (>= 50%), così evito di sparare l'animazione
        // su panel adiacenti già parzialmente in DOM.
        const visibilityRatio = 0.5;
        const w = window.innerWidth, h = window.innerHeight;
        const visW = Math.max(0, Math.min(r.right, w) - Math.max(r.left, 0));
        const visH = Math.max(0, Math.min(r.bottom, h) - Math.max(r.top, 0));
        const alreadyVisible = (visW * visH) / (w * h) > visibilityRatio;
        if (alreadyVisible) {
            fire();
        } else {
            const obs = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    obs.disconnect();
                    fire();
                }
            }, {threshold: 0.5});
            obs.observe(panel);
        }
    } else {
        ScrollTrigger.create({
            trigger: element,
            start,
            once: true,
            onEnter: fire,
        });
    }
}

function gsapTextsAnimation() {
    // data-gsap-scroll-lines: scrub fluido fuori da .js-h-outer, play-on-land dentro.
    gsap.utils.toArray("[data-gsap-scroll-lines]").forEach((container) => {
        const insideHScroll = container.closest('.js-h-outer');
        const targets = resolveTextTargets(container);
        if (!targets.length) return;
        let played = false;

        SplitText.create(targets, {
            type: "words,lines",
            mask: "lines",
            linesClass: "line",
            autoSplit: true,
            onSplit: (instance) => {
                if (insideHScroll) {
                    // Stato iniziale esplicito: container visibile (rimuove il
                    // visibility:hidden del CSS) ma linee giù di 80px finché
                    // l'animazione non parte.
                    gsap.set(container, {autoAlpha: 1});
                    gsap.set(instance.lines, {y: played ? 0 : 80});
                    const tween = gsap.to(instance.lines, {
                        y: 0, stagger: 0.15, ease: "power3.out", duration: 1.2,
                        paused: true,
                    });
                    if (!played) {
                        runOnPanelLand(container, () => {
                            played = true;
                            tween.play();
                        });
                    }
                    return tween;
                }
                return gsap.from(instance.lines, {
                    y: 80,
                    stagger: 0.15,
                    scrollTrigger: {
                        trigger: container,
                        scrub: 2,
                        start: "clamp(top 90%)",
                        end: "clamp(bottom 0%)"
                    }
                });
            }
        });
    });

    gsap.utils.toArray("[data-gsap-title]").forEach((container) => {
        const isHero = container.closest(".intro-home, .hero, [data-gsap-hero]") !== null;
        const delay = getGsapValue(container, "gsapDelay", 0.5);
        const duration = getGsapValue(container, "gsapDuration", 1.2);
        const targets = resolveTextTargets(container);
        if (!targets.length) return;
        let played = false;

        SplitText.create(targets, {
            type: "lines",
            mask: "lines",
            linesClass: "line-gsap",
            autoSplit: true,
            onSplit: (instance) => {
                gsap.set(container, {autoAlpha: 1});
                gsap.set(instance.lines, played ? {skewX: 0, yPercent: 0} : {skewX: 45, yPercent: 102});

                const tl = gsap.timeline({paused: true});
                tl.to(instance.lines, {
                    duration,
                    skewX: 0,
                    yPercent: 0,
                    ease: "power3.out",
                    stagger: {amount: 0.5, from: "center"},
                    onComplete: () => {
                        instance.lines.forEach((line) => {
                            const mask = line.parentElement;
                            if (mask) {
                                gsap.set(mask, {overflow: "visible"});
                            }
                        });
                    },
                });

                if (!played) {
                    if (isHero) {
                        gsap.delayedCall(delay || 0.5, () => {
                            played = true;
                            tl.play();
                        });
                    } else {
                        runOnPanelLand(container, () => {
                            played = true;
                            tl.play();
                        }, {delay, start: "top 80%"});
                    }
                }

                return tl;
            },
        });
    });

    gsap.utils.toArray("[data-gsap-fade]").forEach((element, i) => {
        const delay = getGsapValue(element, "gsapDelay", i * 0.1);
        const duration = getGsapValue(element, "gsapDuration", 1.1);
        gsap.set(element, {autoAlpha: 0, y: 10});
        const anim = gsap.to(element, {
            duration,
            autoAlpha: 1,
            y: 0,
            ease: "power3.out",
            paused: true,
        });
        runOnPanelLand(element, () => anim.play(), {delay, start: "top 85%"});
    });

    gsap.utils.toArray("[data-gsap-fade-up]").forEach((element) => {
        const delay = getGsapValue(element, "gsapDelay", 0);
        const duration = getGsapValue(element, "gsapDuration", 0.8);
        const distance = getGsapValue(element, "gsapDistance", 40);
        gsap.set(element, {autoAlpha: 0, y: distance});
        const anim = gsap.to(element, {
            duration,
            autoAlpha: 1,
            y: 0,
            ease: "power3.out",
            paused: true,
        });
        runOnPanelLand(element, () => anim.play(), {delay, start: "top 85%"});
    });

    gsap.utils.toArray("[data-gsap-fade-words]").forEach((container) => {
        const delay = getGsapValue(container, "gsapDelay", 0);
        const duration = getGsapValue(container, "gsapDuration", 0.8);
        const targets = resolveTextTargets(container);
        if (!targets.length) return;
        let played = false;

        SplitText.create(targets, {
            type: "words",
            mask: "words",
            wordsClass: "word-gsap",
            autoSplit: true,
            onSplit: (instance) => {
                gsap.set(container, {autoAlpha: 1});
                gsap.set(instance.words, played ? {autoAlpha: 1, yPercent: 0} : {autoAlpha: 0, yPercent: 60});
                const tween = gsap.to(instance.words, {
                    duration,
                    autoAlpha: 1,
                    yPercent: 0,
                    ease: "power3.out",
                    stagger: 0.08,
                    paused: true,
                });
                if (!played) {
                    runOnPanelLand(container, () => {
                        played = true;
                        tween.play();
                    }, {delay, start: "top 85%"});
                }
                return tween;
            },
        });
    });

    gsap.utils.toArray("[data-gsap-fade-lines]").forEach((container) => {
        const delay = getGsapValue(container, "gsapDelay", 0);
        const duration = getGsapValue(container, "gsapDuration", 0.4);
        const targets = resolveTextTargets(container);
        if (!targets.length) return;
        let played = false;

        SplitText.create(targets, {
            type: "words,lines",
            mask: "lines",
            linesClass: "line",
            autoSplit: true,
            onSplit: (instance) => {
                gsap.set(container, {autoAlpha: 1});
                gsap.set(instance.lines, played ? {autoAlpha: 1, yPercent: 0} : {autoAlpha: 0, yPercent: 60});
                const tween = gsap.to(instance.lines, {
                    duration,
                    autoAlpha: 1,
                    yPercent: 0,
                    ease: "power3.out",
                    stagger: 0.1,
                    paused: true,
                });
                if (!played) {
                    runOnPanelLand(container, () => {
                        played = true;
                        tween.play();
                    }, {delay, start: "top 90%"});
                }
                return tween;
            },
        });
    });

    gsap.utils.toArray("[data-gsap-title-reveal]").forEach((container) => {
        const delay = getGsapValue(container, "gsapDelay", 0);
        const duration = getGsapValue(container, "gsapDuration", 1.2);
        const targets = resolveTextTargets(container);
        if (!targets.length) return;
        let played = false;

        SplitText.create(targets, {
            type: "lines",
            mask: "lines",
            linesClass: "line",
            autoSplit: true,
            onSplit: (instance) => {
                gsap.set(container, {autoAlpha: 1});
                gsap.set(instance.lines, played ? {y: 0, autoAlpha: 1} : {y: 150, autoAlpha: 0});
                const tl = gsap.timeline({paused: true});
                tl.to(instance.lines, {
                    duration,
                    y: 0,
                    autoAlpha: 1,
                    ease: "power3.out",
                    stagger: 0.20,
                });
                if (!played) {
                    runOnPanelLand(container, () => {
                        played = true;
                        tl.play();
                    }, {delay, start: "top 85%"});
                }
                return tl;
            },
        });
    });
}


export {
    initPanelPin,
    initChildHeroTitles,
    initAwardsVenues,
    initAnimationTitles,
    initAnimationParagraphs,
    initProgressBar,
    gsapTextsAnimation
};
